import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ShoppingCart, Check, Truck, ShieldCheck, Minus, Plus, ChevronLeft, Loader2,
} from "lucide-react";
import { fetchProduct, fetchProducts, formatCAD } from "../lib/api";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { toast } from "../hooks/use-toast";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [qty, setQty] = useState(1);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", id],
    queryFn: () => fetchProduct(id),
  });

  const { data: relatedData } = useQuery({
    queryKey: ["related", product?.menuSlug, product?.id],
    queryFn: () =>
      fetchProducts({
        menu: product.menuSlug,
        limit: 6,
      }),
    enabled: Boolean(product?.menuSlug),
  });

  const related = (relatedData?.items || []).filter((p) => p.id !== product?.id).slice(0, 5);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 flex justify-center text-muted-foreground gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-rp-green" />
        Loading product…
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-lg text-muted-foreground">Product not found.</p>
        <Link to="/boutique" className="text-rp-green font-semibold mt-4 inline-block">
          ← Back to shop
        </Link>
      </div>
    );
  }

  const handleAdd = () => {
    addToCart(product, qty);
    toast({ title: "Added to cart", description: `${qty} × ${product.name}` });
  };

  const buyNow = () => {
    addToCart(product, qty);
    navigate("/panier");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-rp-green mb-6"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="bg-white rounded-xl overflow-hidden border border-border flex items-center justify-center p-6 min-h-[280px]">
          <img
            src={product.image}
            alt={product.name}
            className="w-full max-h-[480px] object-contain"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.currentTarget.src =
                "data:image/svg+xml," +
                encodeURIComponent(
                  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="400" height="400" fill="#f1f5f9"/><text x="200" y="200" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="16">No image</text></svg>`
                );
            }}
          />
        </div>

        <div>
          {product.menuSlug && (
            <Link
              to={`/boutique?menu=${product.menuSlug}`}
              className="text-sm font-semibold uppercase tracking-wide text-rp-green"
            >
              {product.menuName}
            </Link>
          )}
          {product.brand && product.brand !== "—" && (
            <Link
              to={`/boutique?brand=${encodeURIComponent(product.brand)}`}
              className="block text-sm font-semibold text-slate-500 mt-1"
            >
              {product.brand}
            </Link>
          )}
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-slate-900">{product.name}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Part #: <span className="font-semibold text-slate-700">{product.sku}</span>
          </p>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="text-3xl font-extrabold text-slate-900">{formatCAD(product.price)}</span>
            {product.onSale && (
              <span className="text-lg text-slate-400 line-through">{formatCAD(product.regularPrice)}</span>
            )}
            {product.inStock ? (
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600">
                <Check className="h-4 w-4" /> In stock
              </span>
            ) : (
              <span className="text-sm font-semibold text-slate-500">{product.stockStatus || "Special order"}</span>
            )}
          </div>

          {product.description && (
            <p className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line">{product.description}</p>
          )}

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-border rounded-lg">
              <button
                type="button"
                className="p-2 hover:bg-secondary"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-semibold">{qty}</span>
              <button
                type="button"
                className="p-2 hover:bg-secondary"
                onClick={() => setQty((n) => n + 1)}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={handleAdd} className="flex-1 h-12 bg-rp-green hover:bg-rp-green-dark gap-2">
              <ShoppingCart className="h-5 w-5" /> Add to cart
            </Button>
            <Button onClick={buyNow} variant="outline" className="h-12">
              Buy now
            </Button>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2 text-slate-600">
              <Truck className="h-5 w-5 text-rp-green shrink-0 mt-0.5" />
              <span>Canada-wide shipping available</span>
            </div>
            <div className="flex items-start gap-2 text-slate-600">
              <ShieldCheck className="h-5 w-5 text-rp-green shrink-0 mt-0.5" />
              <span>Genuine replacement parts</span>
            </div>
          </div>

          {product.leafName && (
            <p className="mt-4 text-xs text-muted-foreground">
              Category: {product.leafName}
            </p>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Related parts</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
